import { Box, Container, Divider, Link, Stack, Typography } from '@mui/material';
import { OtaFooter } from '@ota/design-system';
import { PublicHeader } from '../components/PublicHeader';

// NOTE: this is a draft privacy policy written to match the app's actual
// data flows (accounts, bookings, Razorpay payments, SMS/WhatsApp OTP and
// notifications, Google Maps). Contact details are placeholders. Have this
// reviewed by whoever handles legal/compliance before treating it as final
// — a privacy policy is a binding document, and this hasn't had legal sign-off.
const SUPPORT_EMAIL = 'otasupport@dataman.in';
const LAST_UPDATED = '24 August 2026';

interface Section {
  title: string;
  body: string[];
}

const SECTIONS: Section[] = [
  {
    title: '1. Introduction',
    body: [
      'This Privacy Policy explains how we collect, use, share, and protect your information when you use our website and services to search for, book, or manage hotel stays, or to list a property with us.',
      'By using our platform, you agree to the practices described in this policy. If you do not agree, please do not use our services.',
    ],
  },
  {
    title: '2. Information We Collect',
    body: [
      'Account information: your name, email address, mobile number, and password (stored in encrypted form) when you sign up or log in.',
      'Booking information: check-in/check-out dates, room and guest details, and special requests you provide when making a reservation.',
      'Payment information: payments are processed by our third-party payment gateway (Razorpay). We do not store your full card, UPI, or net banking credentials on our servers — only a payment reference and status are retained.',
      'Property information: if you list a property with us, we collect property details, room categories, pricing, photographs, and the contact details of the owner or manager.',
      'Communications: messages you send to our support team, and records of transactional emails, SMS, and WhatsApp messages we send you (such as OTPs and booking confirmations).',
      'Usage and device information: pages you visit, actions you take, and basic device/browser information, collected automatically to help us operate and improve the platform.',
    ],
  },
  {
    title: '3. How We Use Your Information',
    body: [
      'To create and manage your account, and to process and confirm your bookings.',
      'To send booking confirmations, payment receipts, OTPs for login, and important updates about your reservation via email, SMS, or WhatsApp.',
      'To provide customer support and respond to your queries or complaints.',
      'To detect and prevent fraud, abuse, and unauthorized access to accounts.',
      'To improve our website, features, and overall user experience.',
      'To comply with legal obligations, such as tax and accounting requirements.',
    ],
  },
  {
    title: '4. Cookies & Similar Technologies',
    body: [
      'We use essential cookies and browser storage to keep you signed in and to remember your session securely. We may also use analytics tools to understand how our website is used, in order to improve it.',
      'You can control or disable cookies through your browser settings; note that some parts of the site (such as staying signed in) may not work correctly if essential cookies are disabled.',
    ],
  },
  {
    title: '5. How We Share Your Information',
    body: [
      'With the property/hotel you book, to the extent necessary to fulfil your reservation (e.g. your name, contact details, and stay dates).',
      'With our payment processor (Razorpay) to process payments and refunds securely.',
      'With SMS/WhatsApp messaging providers and our email service, solely to deliver OTPs, confirmations, and other transactional communications.',
      'With mapping and location service providers (Google Maps/Places), to show property locations and enable location-based search.',
      'With law enforcement or regulatory authorities where required by applicable law, or to protect our rights, users, or the public.',
      'We do not sell your personal information to third parties.',
    ],
  },
  {
    title: '6. Data Security',
    body: [
      'We use industry-standard measures to protect your information, including encrypted storage for sensitive data, encrypted connections (HTTPS) between your browser and our servers, and access controls limiting who can view your data internally.',
      'No method of transmission or storage is completely secure, and we cannot guarantee absolute security. If you believe your account has been compromised, please contact us immediately.',
    ],
  },
  {
    title: '7. Data Retention',
    body: [
      'We retain your account and booking information for as long as your account is active and for a reasonable period afterward, to comply with legal, accounting, or dispute-resolution requirements.',
      'You may request deletion of your account and associated personal data at any time, subject to information we are legally required to retain (such as records of completed transactions).',
    ],
  },
  {
    title: '8. Your Rights',
    body: [
      'Depending on applicable law, you may have the right to access, correct, or request deletion of your personal information, and to withdraw consent for certain processing (such as marketing communications).',
      'To exercise any of these rights, contact us using the details below. We will respond within a reasonable timeframe.',
    ],
  },
  {
    title: '9. Children’s Privacy',
    body: [
      'Our services are not directed at, and should not be used by, individuals under the age of 18. We do not knowingly collect personal information from children.',
    ],
  },
  {
    title: '10. Changes to This Policy',
    body: [
      'We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will post the updated policy on this page with a revised "last updated" date.',
    ],
  },
  {
    title: '11. Contact Us',
    body: [
      'If you have any questions or concerns about this Privacy Policy or how your information is handled, please contact us.',
    ],
  },
];

export function Privacy() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />

      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 6, md: 8 }, flex: 1, px: { xs: 2, sm: 3 } }}>
        {/* Hero */}
        <Stack spacing={1} sx={{ mb: { xs: 4, sm: 5 } }}>
          <Typography variant="overline" color="secondary.main">
            Legal
          </Typography>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 700 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last updated: {LAST_UPDATED}
          </Typography>
        </Stack>

        <Divider sx={{ mb: { xs: 3, sm: 4 } }} />

        {/* Sections */}
        <Stack spacing={4}>
          {SECTIONS.map((section) => (
            <Box key={section.title}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                {section.title}
              </Typography>
              <Stack spacing={1.25}>
                {section.body.map((para, i) => (
                  <Typography key={i} variant="body1" color="text.secondary">
                    {para}
                  </Typography>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>

        <Divider sx={{ my: { xs: 4, sm: 5 } }} />

        <Typography variant="body2" color="text.secondary">
          For any privacy-related questions, write to us at{' '}
          <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover">
            {SUPPORT_EMAIL}
          </Link>
          .
        </Typography>
      </Container>

      <OtaFooter />
    </Box>
  );
}
